/**
 * Reusable <img> elements for the baked webp shots. They are immutable per URL while the grids
 * showing them rebuild on every click, and a fresh element decodes the file again (1280x960 into
 * an 85px tile) and re-requests it wherever the image cache is stricter than Chrome's.
 */

/** `<className>|<chain>` -> the elements minted for it, oldest first. */
const pool = new Map();
/** The grid's keys are catalogue-bounded; a cart chain carries the fabric code, so it is not. */
const MAX_CHAINS = 400;

function acquire(key, eager) {
  let minted = pool.get(key);
  if (!minted) {
    pool.set(key, (minted = []));
    if (pool.size > MAX_CHAINS) evictDetached();
  }
  // An element lives in one place, so a second consumer on screen at once needs its own.
  const free = minted.find((img) => !img.isConnected);
  if (free) return free;

  const img = document.createElement("img");
  img.draggable = false;
  img.decoding = "async";
  img.loading = eager ? "eager" : "lazy";
  if (eager) img.fetchPriority = "high";
  minted.push(img);
  return img;
}

function evictDetached() {
  for (const [key, minted] of pool) {
    if (pool.size <= MAX_CHAINS) return;
    if (minted.every((img) => !img.isConnected)) pool.delete(key);
  }
}

/**
 * Append the shot for `sources` (most specific first) to `parent`, reusing the element that
 * already resolved that chain. `onExhausted` fires only when no source loads.
 */
export function appendImage(parent, sources, { alt = "", className = "", eager = false, onExhausted = null } = {}) {
  // Keyed by class too, so an element never crosses call sites and carries the wrong sizing.
  const key = `${className}|${sources.join("|")}`;
  const img = acquire(key, eager);
  img.alt = alt;
  img.className = className;
  parent.appendChild(img);
  if (img.src) return img; // resolved on an earlier render, or by prefetchImage

  let next = 1;
  img.onerror = () => {
    if (next < sources.length) {
      img.src = sources[next++];
      return;
    }
    pool.delete(key); // nothing resolved; don't hold it, and let a later render retry
    img.onerror = null;
    onExhausted?.(img);
  };
  img.onload = () => { img.onload = null; img.onerror = null; }; // release the captured scope
  img.src = sources[0]; // after appendChild — a detached lazy image never loads
  return img;
}

/** Warm a shot into the pool so the element that eventually shows it is already decoded. */
export function prefetchImage(url, className = "") {
  const img = acquire(`${className}|${url}`, true);
  if (!img.src) img.src = url; // eager, so it loads while still detached
}
